import {
    getDefaultColorOption,
    getBaseColorOptionsByProvider,
    applyColorOrderWithDividers,
} from '@/utils/colorOptions';
import { DateValueType } from 'react-tailwindcss-datepicker';
import { getDateRangeForPeriod, DateRangeOptions } from '@/utils/time';
import { WeekStart } from '@/i18n/weekStart';

// 検索パラメータのインターフェース
export interface SearchParams {
    startDate?: string;
    endDate?: string;
    keyword?: string;
    // 色選択（暫定：フロント側のみ保持）
    color?: ColorOption;
    // カレンダー選択
    calendar?: CalendarOption;
}

// カレンダー選択用インターフェース
export interface CalendarOption {
    value: string;
    label: string;
    isLocked?: boolean;
}

// 色選択用（現状はカレンダーの型と同一）
export type ColorOption = CalendarOption & {
    colorHex?: string;
    isDivider?: boolean;
    originalLabel?: string;
};

// カレンダーリストのインターフェース（OAuthアカウント単位 - 旧形式）
export interface CalendarItem {
    providerId: string;
    providerAccountName: string;
    providerName?: string; // 'google' | 'azure' など
}

// 個別カレンダーのインターフェース（user_calendarsテーブル単位）
export interface IndividualCalendarItem {
    id: number;
    oauthAccountsId: number;
    calendarId: string;
    calendarName: string;
    providerName: string;
    isDefault: boolean;
    accessRole: string | null;
}

/**
 * 祝日・誕生日カレンダーかどうかを判定
 * @param calendar カレンダー情報
 * @returns 祝日・誕生日カレンダーの場合true
 */
export const isHolidayOrBirthdayCalendar = (calendar: IndividualCalendarItem): boolean => {
    const name = calendar.calendarName.toLowerCase();
    const id = calendar.calendarId.toLowerCase();
    const provider = calendar.providerName.toLowerCase();

    // Googleカレンダーの場合（calendarIdに'holiday'が含まれる）
    if (provider === 'google' && id.includes('holiday')) {
        return true;
    }

    // Outlookカレンダーの場合（カレンダー名で判定）
    if (provider === 'azure' || provider.includes('microsoft') || provider.includes('outlook')) {
        const holidayKeywords = ['holiday', 'holidays', '祝日', '休日'];
        const birthdayKeywords = ['birthday', 'birthdays', '誕生日'];
        
        return holidayKeywords.some(keyword => name.includes(keyword)) ||
               birthdayKeywords.some(keyword => name.includes(keyword));
    }

    return false;
};

/**
 * 日付をYYYY-MM-DD形式に変換する（タイムゾーンの問題を回避）
 * @param date 変換する日付
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDateForServer = (date: Date | string): string => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * 初期日付を設定する
 * @param initialParams 初期パラメータ
 * @returns DateValueType形式の日付
 */
interface WeekStartOptions {
    weekStartsOn?: WeekStart;
}

export const getInitialDate = (
    initialParams?: SearchParams,
    options: WeekStartOptions = {},
): DateValueType => {
    const dateRangeOptions: DateRangeOptions = {
        weekStartsOn: options.weekStartsOn,
    };
    if (initialParams?.startDate && initialParams?.endDate) {
        return {
            startDate: new Date(initialParams.startDate),
            endDate: new Date(initialParams.endDate),
        };
    }
    // 共通ロジックで先週の範囲を取得
    const { start, end } = getDateRangeForPeriod('LAST_WEEK', dateRangeOptions);
    return {
        startDate: start,
        endDate: end,
    };
};

/**
 * カレンダーリストからオプション配列を作成（OAuthアカウント単位 - 旧形式）
 * @param calendarList カレンダーリスト
 * @returns Select用のオプション配列
 */
export const createCalendarOptions = (
    calendarList?: CalendarItem[],
    fallbackLabel: string = 'アカウント名なし',
): CalendarOption[] => {
    if (!calendarList || calendarList.length === 0) {
        return [];
    }

    return calendarList.map((calendar) => ({
        value: calendar.providerId,
        label: calendar.providerAccountName || fallbackLabel,
    }));
};

/**
 * 個別カレンダーリストからオプション配列を作成（user_calendarsテーブル単位）
 * 祝日カレンダー（calendarIdに'holiday'が含まれる）は除外
 * @param calendarList 個別カレンダーリスト
 * @returns Select用のオプション配列
 */
export const createIndividualCalendarOptions = (
    calendarList?: IndividualCalendarItem[],
    opts: { lockNonDefault?: boolean } = {},
): CalendarOption[] => {
    if (!calendarList || calendarList.length === 0) {
        return [];
    }

    // 祝日・誕生日カレンダーを除外
    const filteredCalendars = calendarList.filter(
        (cal) => !isHolidayOrBirthdayCalendar(cal),
    );

    // デフォルト（primary）判定用
    const defaultCalendar = filteredCalendars.find((cal) => cal.isDefault);

    // メインカレンダーを先頭に配置するために並び替え
    const sortedCalendars = [...filteredCalendars].sort((a, b) => {
        // メインカレンダー（isDefault=true）を先頭に
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        // それ以外は元の順序を維持
        return 0;
    });

    return sortedCalendars.map((calendar) => {
        // カレンダー名をそのまま表示
        return {
            value: String(calendar.id), // カレンダーID（user_calendarsテーブルのid）を使用
            label: calendar.calendarName,
            isLocked:
                opts.lockNonDefault === true &&
                defaultCalendar &&
                !calendar.isDefault &&
                calendar.id !== defaultCalendar.id,
        };
    });
};

/**
 * 個別カレンダーリストからデフォルトカレンダー（is_default=true）を取得
 * @param calendarList 個別カレンダーリスト
 * @returns デフォルトのカレンダーオプション
 */
export const getDefaultIndividualCalendarOption = (
    calendarList?: IndividualCalendarItem[],
): CalendarOption => {
    if (!calendarList || calendarList.length === 0) {
        return { value: '', label: '' };
    }

    // 祝日・誕生日カレンダーを除外
    const filteredCalendars = calendarList.filter(
        (cal) => !isHolidayOrBirthdayCalendar(cal),
    );

    if (filteredCalendars.length === 0) {
        return { value: '', label: '' };
    }

    // is_default=trueのカレンダーを探す
    const defaultCalendar = filteredCalendars.find((cal) => cal.isDefault);
    if (defaultCalendar) {
        return {
            value: String(defaultCalendar.id),
            label: defaultCalendar.calendarName,
        };
    }

    // それもない場合は最初のカレンダー
    const firstCalendar = filteredCalendars[0];
    return {
        value: String(firstCalendar.id),
        label: firstCalendar.calendarName,
    };
};

/**
 * カレンダーリストからデフォルト値を取得
 * @param calendarList カレンダーリスト
 * @returns デフォルトのカレンダーオプション
 */
export const getDefaultCalendarOption = (
    calendarList?: CalendarItem[],
    fallbackLabel: string = 'アカウント名なし',
): CalendarOption => {
    if (!calendarList || calendarList.length === 0) {
        return { value: '', label: '' };
    }

    return {
        value: calendarList[0].providerId,
        label: calendarList[0].providerAccountName || fallbackLabel,
    };
};

/**
 * Datepickerのショートカット設定を取得
 * @returns ショートカット設定
 */
export interface ShortcutLabels {
    today: string;
    yesterday: string;
    past7Days: string;
    past14Days?: string;
    past30Days: string;
    past3Months?: string;
    past6Months?: string;
    past12Months?: string;
    pastWeek: string;
    currentWeek: string;
    pastMonth: string;
    currentMonth: string;
}

export const getDatepickerShortcuts = (
    isPaid: boolean = false,
    labels?: ShortcutLabels,
    options: WeekStartOptions = {},
) => {
    const dateRangeOptions: DateRangeOptions = {
        weekStartsOn: options.weekStartsOn,
    };
    const l = {
        today: labels?.today ?? 'Today',
        yesterday: labels?.yesterday ?? 'Yesterday',
        past7Days: labels?.past7Days ?? 'Past 7 days',
        past14Days: labels?.past14Days ?? 'Past 14 days',
        past30Days: labels?.past30Days ?? 'Past 30 days',
        past3Months: labels?.past3Months ?? 'Past 3 months',
        past6Months: labels?.past6Months ?? 'Past 6 months',
        past12Months: labels?.past12Months ?? 'Past 12 months',
        pastWeek: labels?.pastWeek ?? 'Last week',
        currentWeek: labels?.currentWeek ?? 'This week',
        pastMonth: labels?.pastMonth ?? 'Last month',
        currentMonth: labels?.currentMonth ?? 'This month',
    };

    return {
        today: l.today,
        yesterday: l.yesterday,
        past7Days: {
            text: l.past7Days,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('PAST_7_DAYS', dateRangeOptions);
                return { start, end };
            })(),
        },
        past14Days: {
            text: l.past14Days,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('PAST_14_DAYS', dateRangeOptions);
                return { start, end };
            })(),
        },
        past30Days: {
            text: l.past30Days,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('PAST_30_DAYS', dateRangeOptions);
                return { start, end };
            })(),
        },
        /* 有料プラン用の期間（非有料プランでも表示するが、ロックアイコン付き） */
                  past3Months: {
            text: isPaid ? l.past3Months : `${l.past3Months} 🔒`,
                      period: (() => {
                          const { start, end } = getDateRangeForPeriod(
                              'PAST_3_MONTHS',
                              dateRangeOptions,
                          );
                          return { start, end };
                      })(),
                  },
                  past6Months: {
            text: isPaid ? l.past6Months : `${l.past6Months} 🔒`,
                      period: (() => {
                          const { start, end } = getDateRangeForPeriod(
                              'PAST_6_MONTHS',
                              dateRangeOptions,
                          );
                          return { start, end };
                      })(),
                  },
                  past12Months: {
            text: isPaid ? l.past12Months : `${l.past12Months} 🔒`,
                      period: (() => {
                          const { start, end } = getDateRangeForPeriod(
                              'PAST_12_MONTHS',
                              dateRangeOptions,
                          );
                          return { start, end };
                      })(),
                  },
        pastWeek: {
            text: l.pastWeek,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('LAST_WEEK', dateRangeOptions);
                return { start, end };
            })(),
        },
        currentWeek: {
            text: l.currentWeek,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('THIS_WEEK', dateRangeOptions);
                return { start, end };
            })(),
        },
        pastMonth: l.pastMonth,
        currentMonth: l.currentMonth,
    };
};

/**
 * 色選択のデフォルト値（指定なし）
 */
export { getDefaultColorOption };

/**
 * プロバイダー名から色オプションの基本リストを取得
 */
export { getBaseColorOptionsByProvider };

/**
 * 並び替え: 指定なし → 仕切り → すべて → 仕切り → 使用順(MRU) → 仕切り → その他
 */
export { applyColorOrderWithDividers };
